import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { createCase, updateCase } from "@/features/cases/api";
import {
  CASE_STATUS_OPTIONS,
  type Case,
  type CasePayload,
  type CaseStatus,
  type ResolutionType,
  RESOLUTION_TYPE_OPTIONS,
} from "@/features/cases/types";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";
import { applyApiErrors } from "@/lib/form-errors";

interface CaseFormValues {
  resolutionType: ResolutionType;
  status: CaseStatus;
  assignedPractitioner: string;
  balance: string;
  taxYears: string;
  targetDate: string;
  followupDate: string;
  notes: string;
}

const validationSchema = Yup.object({
  resolutionType: Yup.string().required("Resolution type is required"),
  status: Yup.string().required("Status is required"),
  balance: Yup.string()
    .matches(/^\d*(\.\d{0,2})?$/, "Enter a dollar amount")
    .test("non-negative", "Balance can't be negative", (v) => !v || Number(v) >= 0),
});

function toInitialValues(c: Case | null, defaultPractitioner: string): CaseFormValues {
  return {
    resolutionType: c?.resolutionType ?? "IA",
    status: c?.status ?? "ACTIVE",
    // New case pre-selects the user's default practitioner (§8); editing keeps
    // whatever the case already has.
    assignedPractitioner: c ? (c.assignedPractitioner ?? "") : defaultPractitioner,
    balance: c ? (c.balanceCents / 100).toFixed(2) : "",
    taxYears: c?.taxYears ?? "",
    targetDate: c?.targetDate ?? "",
    followupDate: c?.followupDate ?? "",
    notes: c?.notes ?? "",
  };
}

function toPayload(v: CaseFormValues, clientId: string, creating: boolean): CasePayload {
  const payload: CasePayload = {
    resolution_type: v.resolutionType,
    status: v.status,
    assigned_practitioner: v.assignedPractitioner || null,
    balance: v.balance === "" ? "0" : v.balance,
    tax_years: v.taxYears.trim(),
    target_date: v.targetDate || null,
    followup_date: v.followupDate || null,
    notes: v.notes,
  };
  if (creating) payload.client = clientId;
  return payload;
}

interface CaseFormDialogProps {
  clientId: string;
  /** `null` = open a new case; otherwise edit this case. */
  caseRecord: Case | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function CaseFormDialog({
  clientId,
  caseRecord,
  open,
  onOpenChange,
  onSaved,
}: CaseFormDialogProps) {
  const editing = caseRecord !== null;
  const defaultPractitioner = useAuthStore((s) => s.user?.default_practitioner ?? "");
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    listPractitioners()
      .then((list) => active && setPractitioners(list))
      .catch(() => active && setPractitioners([]));
    return () => {
      active = false;
    };
  }, [open]);

  const practitionerOptions = [
    { value: "", label: "Unassigned" },
    ...practitioners.map((p) => ({ value: p.id, label: p.name })),
  ];

  async function handleSubmit(values: CaseFormValues, bag: FormikHelpers<CaseFormValues>) {
    try {
      if (editing) {
        await updateCase(caseRecord.id, toPayload(values, clientId, false));
      } else {
        await createCase(toPayload(values, clientId, true));
      }
      toast.success(editing ? "Case updated" : "Case opened");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save case." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Case" : "Open Case"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={toInitialValues(caseRecord, defaultPractitioner)}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <SelectField
                  name="resolutionType"
                  label="Resolution Type"
                  options={RESOLUTION_TYPE_OPTIONS}
                />
                <SelectField name="status" label="Status" options={CASE_STATUS_OPTIONS} />
              </div>
              <SelectField
                name="assignedPractitioner"
                label="Assigned Practitioner"
                options={practitionerOptions}
                placeholder="Unassigned"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="balance"
                  label="Balance ($)"
                  labelClassName={FIELD_LABEL}
                  inputMode="decimal"
                  placeholder="0.00"
                />
                <FormField
                  name="taxYears"
                  label="Tax Years"
                  labelClassName={FIELD_LABEL}
                  placeholder="e.g. 2019, 2020"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="targetDate"
                  label="Target Date"
                  labelClassName={FIELD_LABEL}
                  type="date"
                />
                <FormField
                  name="followupDate"
                  label="Follow-up Date"
                  labelClassName={FIELD_LABEL}
                  type="date"
                />
              </div>
              <FormField
                name="notes"
                label="Notes"
                labelClassName={FIELD_LABEL}
                placeholder="Optional"
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Open Case"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
